import { useState } from "react";
import { Search, RefreshCw, Bell, ChevronDown, User, Zap, Settings, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export const Navigation = ({ 
  userProfile, 
  aiLevel, 
  onProfileChange, 
  onAiLevelChange,
  onRefresh,
  isRefreshing,
  onNavClick,
  onSearchOpen,
  onNotificationsOpen
}) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLevelMenu, setShowLevelMenu] = useState(false);

  const profiles = [
    { value: "leader", label: "Leader" },
    { value: "user", label: "Team Member" }
  ];

  const levels = [
    { value: "beginner", label: "Beginner" },
    { value: "intermediate", label: "Intermediate" },
    { value: "advanced", label: "Advanced" }
  ];

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#14B8A6] flex items-center justify-center">
              <svg className="w-5 h-5 text-[#050B14]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <h1 className="font-headline font-semibold text-base md:text-lg text-[#F8FAFC] leading-tight tracking-tight whitespace-nowrap" data-testid="app-title">AI Know Board</h1>
              <p className="text-xs text-[#94A3B8] hidden md:block">Your AI Intelligence Hub</p>
            </div>
          </div>

          {/* Center Nav - Desktop */}
          <nav className="hidden md:flex items-center gap-1">
            <a href="/" className="px-4 py-2 text-sm font-medium text-[#F8FAFC] rounded-md hover:bg-white/5 transition-colors">
              Dashboard
            </a>
            <button 
              onClick={() => onNavClick && onNavClick("coach")}
              className="px-4 py-2 text-sm font-medium text-[#94A3B8] rounded-md hover:bg-white/5 hover:text-[#F8FAFC] transition-colors"
              data-testid="nav-link-coach"
            >
              AI Coach
            </button>
            <button 
              onClick={() => onNavClick && onNavClick("learning")}
              className="px-4 py-2 text-sm font-medium text-[#94A3B8] rounded-md hover:bg-white/5 hover:text-[#F8FAFC] transition-colors"
              data-testid="nav-link-learning"
            >
              Learning
            </button>
          </nav>

          {/* Right Controls */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            {/* Search */}
            <button
              onClick={onSearchOpen}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-all text-sm text-[#94A3B8] hidden md:flex"
              data-testid="search-button"
            >
              <Search className="w-4 h-4" />
              <span className="text-xs">Search...</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/[0.06] text-[10px] text-[#94A3B8] font-mono">
                <span className="text-[10px]">Ctrl</span>K
              </kbd>
            </button>

            {/* Mobile search */}
            <button
              onClick={onSearchOpen}
              className="p-2 rounded-md hover:bg-white/[0.06] transition-colors md:hidden"
              data-testid="search-button-mobile"
            >
              <Search className="w-5 h-5 text-[#94A3B8]" />
            </button>

            {/* Notifications */}
            <button
              onClick={onNotificationsOpen}
              className="p-2 rounded-md hover:bg-white/[0.06] transition-colors relative"
              data-testid="notifications-button"
            >
              <Bell className="w-5 h-5 text-[#94A3B8]" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#14B8A6] rounded-full" />
            </button>

            {/* Refresh */}
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`p-2 rounded-md transition-all ${
                isRefreshing 
                  ? 'bg-[#14B8A6]/10 cursor-not-allowed' 
                  : 'hover:bg-white/[0.06]'
              }`}
              title="Refresh content from external feeds"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin text-[#14B8A6]' : 'text-[#94A3B8]'}`} />
            </button>

            {/* Profile Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowProfileMenu(!showProfileMenu);
                  setShowLevelMenu(false);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-all text-sm"
                data-testid="profile-selector"
              >
                <User className="w-4 h-4 text-[#94A3B8]" />
                <span className="hidden sm:inline capitalize text-[#F8FAFC]">{userProfile}</span>
                <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
              </button>
              
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg bg-[#112240] border border-white/10 shadow-xl py-1 z-50" data-testid="profile-menu">
                  {profiles.map(profile => (
                    <button
                      key={profile.value}
                      onClick={() => {
                        onProfileChange(profile.value);
                        setShowProfileMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        userProfile === profile.value 
                          ? 'bg-[#14B8A6]/10 text-[#14B8A6] font-medium' 
                          : 'text-[#94A3B8] hover:bg-white/[0.04] hover:text-[#F8FAFC]'
                      }`}
                      data-testid={`profile-option-${profile.value}`}
                    >
                      {profile.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* AI Level Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowLevelMenu(!showLevelMenu);
                  setShowProfileMenu(false);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-all text-sm"
                data-testid="level-selector"
              >
                <Zap className="w-4 h-4 text-[#14B8A6]" />
                <span className="hidden sm:inline capitalize text-[#F8FAFC]">{aiLevel}</span>
                <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
              </button>
              
              {showLevelMenu && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg bg-[#112240] border border-white/10 shadow-xl py-1 z-50" data-testid="level-menu">
                  {levels.map(level => (
                    <button
                      key={level.value}
                      onClick={() => {
                        onAiLevelChange(level.value);
                        setShowLevelMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        aiLevel === level.value 
                          ? 'bg-[#14B8A6]/10 text-[#14B8A6] font-medium' 
                          : 'text-[#94A3B8] hover:bg-white/[0.04] hover:text-[#F8FAFC]'
                      }`}
                      data-testid={`level-option-${level.value}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Avatar with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/10 hover:ring-[#14B8A6]/30 transition-all cursor-pointer" data-testid="user-avatar">
                  <img 
                    src="https://ui-avatars.com/api/?name=Alex+User&background=6D28D9&color=fff&size=36" 
                    alt="User Profile"
                    className="w-full h-full object-cover"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-[#112240] border-white/10">
                <DropdownMenuLabel className="text-[#F8FAFC]">
                  <div>
                    <p className="text-sm font-medium">Alex User</p>
                    <p className="text-xs text-[#94A3B8] font-normal capitalize">{userProfile} Profile</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem className="text-[#94A3B8] hover:text-[#F8FAFC] focus:bg-white/[0.06] focus:text-[#F8FAFC] cursor-pointer" data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem className="text-[#94A3B8] hover:text-[#F8FAFC] focus:bg-white/[0.06] focus:text-[#F8FAFC] cursor-pointer" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};
